import Plugin from "@swup/plugin";

/**
 * Es gibt zwar ein Plugin für den head aber keins für die body scripts,
 * deswegen habe ich die Funktionen aus dem head Plugin kopiert und angepasst.
 */
export default class SwupMergeAssetsPlugin extends Plugin {
  constructor(options) {
    super();

    this.name = "SwupMergeAssetsPlugin";

    this.requires = { swup: ">=4" };

    this.defaults = {
      containers: [],
      persistTags: false,
      persistAssets: false,
      awaitAssets: false,
      timeout: 3000,
    };

    this.options = { ...this.defaults, ...options };

    // persistAssets is a shortcut for: persistTags with a default asset selector for scripts & styles
    if (this.options.persistAssets && !this.options.persistTags) {
      this.options.persistTags = "link[rel=stylesheet], script[src], style";
    }
  }

  mount() {
    this.before("content:replace", this.updateAssetContainer);
  }

  updateAssetContainer = async (visit, { page: { html } }) => {
    const newDocument = new DOMParser().parseFromString(html, "text/html");

    let assetLoadPromises;
    this.newScriptsLoaded = 0;
    this.options.containers.forEach((selector) => {
      const newContent = newDocument.querySelector(selector);
      const oldContent = document.querySelector(selector);
      const { added } = this.mergeContents(oldContent, newContent, {
        shouldPersist: (el) => this.isPersistentTag(el),
      });

      assetLoadPromises = this.waitForAssets(added, this.options.timeout);
    });

    if (this.options.awaitAssets) {
      if (assetLoadPromises.length) {
        this.swup.log(`Waiting for ${assetLoadPromises.length} assets to load`);
        await Promise.all(assetLoadPromises);
      }
    }
  };

  isPersistentTag(el) {
    const { persistTags } = this.options;

    if (typeof persistTags === "function") {
      return persistTags(el);
    }

    if (typeof persistTags === "string") {
      return el.matches(persistTags);
    }

    return Boolean(persistTags);
  }

  /* -------------------------- */
  /* ----- merge contents ------- */
  /* -------------------------- */
  mergeContents(
    currentContent,
    newContent,
    { shouldPersist = () => false } = {},
  ) {
    const currentTags = Array.from(currentContent.children);
    const newChildren = Array.from(newContent.children);

    const addTags = this.getTagsToAdd(currentTags, newChildren);
    const removeTags = this.getTagsToRemove(currentTags, newChildren);

    // Remove tags in reverse to keep indexes, keep persistant elements
    removeTags
      .reverse()
      .filter(({ el }) => this.shouldManageTag(el))
      .filter(({ el }) => !shouldPersist(el))
      .forEach(({ el }) => currentContent.removeChild(el));

    // Insert tag *after* previous version of itself to preserve JS variable scope and CSS cascade
    addTags
      .filter(({ el }) => this.shouldManageTag(el))
      .forEach(({ el, index = 0 }) => {
        if (el.tagName === "SCRIPT") {
          const newEl = document.createElement("script");
          [...el.attributes].forEach(({ name, value }) =>
            newEl.setAttribute(name, value),
          );

          el = newEl;
        }
        currentContent.insertBefore(
          el,
          currentContent.children[index + 1] || null,
        );

        if (el.tagName === "SCRIPT") {
          el.onload = () => {
            this.newScriptsLoaded++;
          };
        }
      });

    // ich gehe bisher davon aus, dass das nicht nötig ist die Reihenfolge zu ändern
    // const newList = [...currentContent.children]
    //   .sort((a, b) => {
    //     // is a luther
    //     if (a.id.indexOf("luther") !== -1) {
    //       return 1;
    //     }
    //     return -1;
    //   })
    //   .forEach((node) => currentContent.appendChild(node));

    return {
      removed: removeTags.map(({ el }) => el),
      added: addTags.map(({ el }) => el),
    };
  }

  getTagsToRemove(currentEls, newEls) {
    const tagsToRemove = [];
    currentEls.forEach((el) => {
      const isAmongNew = newEls.some((newEl) => this.compareTags(el, newEl));
      if (!isAmongNew) {
        tagsToRemove.push({ el: el });
      }
    });

    return tagsToRemove;
  }

  getTagsToAdd(currentEls, newEls) {
    return newEls.reduce((tags, el, index) => {
      const isAmongCurrent = currentEls.some((currentEl) =>
        this.compareTags(el, currentEl),
      );
      if (!isAmongCurrent) {
        tags.push({ el, index });
      }
      return tags;
    }, []);
  }

  shouldManageTag(el) {
    // Let swup manage the title tag
    if (el.localName === "title") {
      return false;
    }
    // Leave swup theme styles untouched
    if (el.matches("[data-swup-theme]")) {
      return false;
    }
    return true;
  }

  compareTags(oldTag, newTag) {
    return oldTag.outerHTML === newTag.outerHTML;
  }

  /* -------------------------- */
  /* ----- wait for assets ----- */
  /* -------------------------- */
  waitForStylesheet(element, timeoutMs = 0) {
    const isLoaded = ({ href }) => {
      return Array.from(document.styleSheets)
        .map(({ href }) => href)
        .includes(href);
    };

    const whenLoaded = (cb) => {
      if (isLoaded(element)) {
        cb();
      } else {
        setTimeout(() => whenLoaded(cb), 10);
      }
    };

    return new Promise((resolve) => {
      whenLoaded(resolve);
      if (timeoutMs > 0) {
        setTimeout(resolve, timeoutMs);
      }
    });
  }

  isStylesheet(el) {
    return el.matches("link[rel=stylesheet][href]");
  }

  /**
   * Das head Plugin von Swup hat keine Funktion auf die Scripte zu warten.
   * Leider klappt das onload event nur, wenn ich es direkt hinter dem einfügen mit insertBefore mache,
   * darum hier die Lösung mit dem Counter.
   */
  waitForScripts(elements) {
    return new Promise((resolve) => {
      const waitingInterval = setInterval(() => {
        if (this.newScriptsLoaded >= elements.length) {
          resolve();
          clearInterval(waitingInterval);
        }
      }, 10);
    });
  }

  isScript(el) {
    return el.matches("script[src]");
  }

  waitForAssets(elements, timeoutMs = 0) {
    const stylePromises = elements
      .filter(this.isStylesheet)
      .map((el) => this.waitForStylesheet(el, timeoutMs));

    const scriptsToLoad = elements.filter(this.isScript);
    const scriptsPromise = this.waitForScripts(scriptsToLoad, timeoutMs);

    return [...stylePromises, scriptsPromise];
  }
}
